export interface CookiePreferences {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

const STORAGE_KEY = 'cookie-preferences';

/* =========================
   Read / Write
========================= */

export function getCookiePreferences(): CookiePreferences | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CookiePreferences) : null;
  } catch {
    return null;
  }
}

export function setCookiePreferences(prefs: CookiePreferences) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  try {
    // Legacy event for backwards compatibility
    window.dispatchEvent(new Event('cookieConsentChange'));
  } catch {
    // ignore
  }
  try {
    // Modern, detailed event consumers can use
    window.dispatchEvent(new CustomEvent('cookie-preferences-changed', { detail: prefs }));
  } catch {
    // ignore
  }
  applyConsentMode();
}

/* =========================
   Guards
========================= */

export function isAnalyticsAllowed(): boolean {
  return Boolean(getCookiePreferences()?.analytics);
}

export function isMarketingAllowed(): boolean {
  return Boolean(getCookiePreferences()?.marketing);
}

export function isThirdPartyAllowed(): boolean {
  return isMarketingAllowed();
}

export function isFunctionalAllowed(): boolean {
  return Boolean(getCookiePreferences()?.functional);
}

/* =========================
   Cleanup helpers
========================= */

export function clearFunctionalStorage() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem('theme');
    localStorage.removeItem('preferred-locale');
  } catch {
    // ignore storage errors (e.g., unavailable)
  }
}

/* =========================
   Side effects (light)
========================= */

export function applyConsentMode() {
  if (typeof window === 'undefined') return;

  const prefs = getCookiePreferences();
  if (!prefs) return;

  type Gtag = (...args: unknown[]) => void;
  const w = window as unknown as { gtag?: Gtag };
  const gtag = w.gtag;
  if (typeof gtag !== 'function') return;

  gtag('consent', 'update', {
    analytics_storage: prefs.analytics ? 'granted' : 'denied',
    ad_storage: prefs.marketing ? 'granted' : 'denied',
    ad_user_data: prefs.marketing ? 'granted' : 'denied',
    ad_personalization: prefs.marketing ? 'granted' : 'denied',
    functionality_storage: prefs.functional ? 'granted' : 'denied',
    security_storage: 'granted',
  } as Record<string, unknown>);
}
